import { CircularProgress, Dialog, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { useMascara } from "../../../../../core/hooks/useMascara";
import { useClientes } from "../../../../../clientes/hooks/useClientes";
import {
    ICliente,
    IndicadorInscricaoEstadual,
    IndicadorInscricaoEstadualDescricao,
    TipoPessoaCliente
} from "../../../../../clientes/interface/ICliente";
import { IEndereco } from "../../../../../core/interfaces/IEndereco";
import { IDadosEmitirNFe } from "../../../../interface/IDadosEmitirNFe";
import { validarCpf } from "../../../../../core/utils/validarCpf";
import { validarCnpj } from "../../../../../core/utils/validarCnpj";
import { EmitirNFeForm } from "./styles";

const NATUREZA_OPERACAO_PADRAO = "VENDA DE MERCADORIAS";

interface EmitirNFeModalProps {
    open: boolean;
    loading: boolean;
    /** Cliente vinculado ao pedido — usado para pré-preencher o destinatário. */
    clienteUuid?: string;
    cancelar: () => void;
    concluir: (dados: IDadosEmitirNFe) => void;
}

const enderecoVazio: IEndereco = {
    rua: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    cep: "",
};

export const EmitirNFeModal = ({ open, loading, clienteUuid, cancelar, concluir }: EmitirNFeModalProps) => {
    const snackbar = useSnackbar();
    const { mascaraCPF, mascaraCNPJ, mascaraCEP } = useMascara();
    const { buscarPorUuid, listarEnderecos } = useClientes();

    const [tipoPessoa, setTipoPessoa] = useState<"CPF" | "CNPJ">("CNPJ");
    const [numeroPessoa, setNumeroPessoa] = useState("");
    const [nomePessoa, setNomePessoa] = useState("");
    const [inscricaoEstadual, setInscricaoEstadual] = useState("");
    const [indicadorInscricaoEstadual, setIndicadorInscricaoEstadual] = useState<IndicadorInscricaoEstadual>(
        IndicadorInscricaoEstadual.CONTRIBUINTE
    );
    const [endereco, setEndereco] = useState<IEndereco>(enderecoVazio);
    const [naturezaOperacao, setNaturezaOperacao] = useState(NATUREZA_OPERACAO_PADRAO);
    const [informacoesAdicionais, setInformacoesAdicionais] = useState("");
    const [carregandoCliente, setCarregandoCliente] = useState(false);

    useEffect(() => {
        if (!open || !clienteUuid) {
            return;
        }

        const preencherComCliente = async () => {
            setCarregandoCliente(true);
            try {
                const cliente = await buscarPorUuid(clienteUuid);
                if (!cliente) {
                    return;
                }

                aplicarCliente(cliente);

                const enderecos = cliente.enderecos?.length
                    ? cliente.enderecos
                    : await listarEnderecos(clienteUuid);

                if (enderecos?.length) {
                    const [primeiroEndereco] = enderecos;
                    setEndereco({
                        ...primeiroEndereco,
                        cep: primeiroEndereco.cep ? mascaraCEP(primeiroEndereco.cep) : "",
                    });
                }
            } finally {
                setCarregandoCliente(false);
            }
        };

        preencherComCliente();
    }, [open, clienteUuid]);

    const aplicarCliente = (cliente: ICliente) => {
        const juridica = cliente.tipoPessoa === TipoPessoaCliente.JURIDICA;

        setTipoPessoa(juridica ? "CNPJ" : "CPF");
        setNomePessoa(juridica ? (cliente.razaoSocial ?? cliente.nome) : cliente.nome);
        setNumeroPessoa(juridica
            ? mascaraCNPJ(cliente.cnpj ?? "")
            : mascaraCPF(cliente.cpf ?? "")
        );
        setInscricaoEstadual(cliente.inscricaoEstadual ?? "");
        setIndicadorInscricaoEstadual(
            cliente.indicadorInscricaoEstadual ?? (juridica
                ? IndicadorInscricaoEstadual.CONTRIBUINTE
                : IndicadorInscricaoEstadual.NAO_CONTRIBUINTE)
        );
    };

    const handleNumeroPessoa = (valor: string) => {
        setNumeroPessoa(tipoPessoa === "CPF" ? mascaraCPF(valor) : mascaraCNPJ(valor));
    };

    const handleEndereco = (campo: keyof IEndereco, valor: string) => {
        setEndereco(atual => ({
            ...atual,
            [campo]: campo === "cep" ? mascaraCEP(valor) : valor,
        }));
    };

    const handleCancelar = () => {
        cancelar();
    };

    const validar = (): boolean => {
        if (tipoPessoa === "CPF" && !validarCpf(numeroPessoa, true)) {
            snackbar("CPF do destinatário inválido", { severity: "error" });
            return false;
        }

        if (tipoPessoa === "CNPJ" && !validarCnpj(numeroPessoa, true)) {
            snackbar("CNPJ do destinatário inválido", { severity: "error" });
            return false;
        }

        if (!nomePessoa?.trim()) {
            snackbar("Informe o nome ou a razão social do destinatário", { severity: "error" });
            return false;
        }

        if (indicadorInscricaoEstadual === IndicadorInscricaoEstadual.CONTRIBUINTE && !inscricaoEstadual?.trim()) {
            snackbar("Inscrição estadual é obrigatória para contribuintes do ICMS", { severity: "error" });
            return false;
        }

        const camposObrigatorios: Array<[keyof IEndereco, string]> = [
            ["rua", "Logradouro"],
            ["numero", "Número"],
            ["bairro", "Bairro"],
            ["cidade", "Município"],
            ["uf", "UF"],
            ["cep", "CEP"],
        ];

        for (const [campo, rotulo] of camposObrigatorios) {
            if (!endereco[campo]?.toString().trim()) {
                snackbar(`${rotulo} do destinatário é obrigatório na NF-e`, { severity: "error" });
                return false;
            }
        }

        return true;
    };

    const handleEmitir = () => {
        if (!open || loading || !validar()) {
            return;
        }

        concluir({
            tipoPessoa,
            numeroPessoa,
            nomePessoa,
            inscricaoEstadual,
            indicadorInscricaoEstadual,
            endereco,
            naturezaOperacao: naturezaOperacao?.trim() || NATUREZA_OPERACAO_PADRAO,
            informacoesAdicionais: informacoesAdicionais?.trim() || undefined,
        });
    };

    return (
        <Dialog open={open} onClose={handleCancelar} maxWidth="md" sx={{ zIndex: 99999 }}>
            <EmitirNFeForm>
                <Typography variant="h6">Emitir NF-e</Typography>
                <Typography variant="caption" color="text.secondary">
                    A autorização pela SEFAZ pode levar alguns segundos.
                </Typography>

                {carregandoCliente && <CircularProgress size={20} />}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            label="Tipo de pessoa"
                            value={tipoPessoa}
                            onChange={(e) => setTipoPessoa(e.target.value as "CPF" | "CNPJ")}
                            SelectProps={{ MenuProps: { style: { zIndex: 999999 } } }}
                        >
                            <MenuItem value="CNPJ">CNPJ</MenuItem>
                            <MenuItem value="CPF">CPF</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            label={`${tipoPessoa}*`}
                            value={numeroPessoa}
                            onChange={(e) => handleNumeroPessoa(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={tipoPessoa === "CNPJ" ? "Razão social*" : "Nome*"}
                            value={nomePessoa}
                            onChange={(e) => setNomePessoa(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <TextField
                            select
                            fullWidth
                            label="Indicador de inscrição estadual"
                            value={indicadorInscricaoEstadual}
                            onChange={(e) => setIndicadorInscricaoEstadual(e.target.value as IndicadorInscricaoEstadual)}
                            SelectProps={{ MenuProps: { style: { zIndex: 999999 } } }}
                        >
                            {Object.values(IndicadorInscricaoEstadual).map(indicador => (
                                <MenuItem key={indicador} value={indicador}>
                                    {IndicadorInscricaoEstadualDescricao[indicador]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {indicadorInscricaoEstadual === IndicadorInscricaoEstadual.CONTRIBUINTE && (
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                label="Inscrição estadual*"
                                value={inscricaoEstadual}
                                onChange={(e) => setInscricaoEstadual(e.target.value)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="subtitle2">Endereço do destinatário</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            label="Logradouro*"
                            value={endereco.rua ?? ""}
                            onChange={(e) => handleEndereco("rua", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Número*"
                            value={endereco.numero ?? ""}
                            onChange={(e) => handleEndereco("numero", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Complemento"
                            value={endereco.complemento ?? ""}
                            onChange={(e) => handleEndereco("complemento", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Bairro*"
                            value={endereco.bairro ?? ""}
                            onChange={(e) => handleEndereco("bairro", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Município*"
                            value={endereco.cidade ?? ""}
                            onChange={(e) => handleEndereco("cidade", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            label="UF*"
                            inputProps={{ maxLength: 2 }}
                            value={endereco.uf ?? ""}
                            onChange={(e) => handleEndereco("uf", e.target.value.toUpperCase())}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            label="CEP*"
                            value={endereco.cep ?? ""}
                            onChange={(e) => handleEndereco("cep", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Natureza da operação"
                            value={naturezaOperacao}
                            onChange={(e) => setNaturezaOperacao(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Informações adicionais"
                            value={informacoesAdicionais}
                            onChange={(e) => setInformacoesAdicionais(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Stack flexDirection={"row"} sx={{ width: "100%" }} justifyContent={"space-between"}>
                    <Button
                        variant="outlined"
                        style={{ width: "47%" }}
                        type="button"
                        onClick={handleCancelar}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        style={{ width: "47%" }}
                        type="button"
                        onClick={handleEmitir}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress color="inherit" size={24} /> : "Emitir"}
                    </Button>
                </Stack>
            </EmitirNFeForm>
        </Dialog>
    );
};
